import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FacebookService,
  FinnService,
  PaymentService,
  UserService,
  Utils,
} from '../../../lib/services';
import { FinnAdvertisement } from '../../../lib/classes/finn/finnAdvertisement';
import { FinnIndustry } from '../../../lib/classes/finn/FinnIndustry';
import { FinnSector } from '../../../lib/classes/finn/FinnSector';
import { FinnDuration } from '../../../lib/classes/finn/FinnDuration';
import { FinnOccupation } from '../../../lib/classes/finn/FinnOccupation';
import { FinnContact } from '../../../lib/classes/finn/FinnContact';
import { TranslateService } from '@ngx-translate/core';
import { FinnTag } from '../../../lib/classes/finn/FinnTag';
import { ContentService } from '../../../lib/services/contentService';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  MatAutocompleteTrigger,
  MatDialogRef,
  MatSnackBar,
} from '@angular/material';
import { map, startWith, take } from 'rxjs/operators';
import * as Quill from 'quill';
import { Subscription } from 'rxjs';
import { getSelectedFinnForm } from '../../reducers';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../reducers';
import { EditorText } from '../../../shared-module/components/mj-text-editor/mj-text-editor.component';

@Component({
  selector: 'mj-finn-form',
  templateUrl: './finn-form.template.html',
  styleUrls: ['./finn-form.style.scss'],
})
export class FinnFormComponent implements OnInit, OnDestroy {
  private finnAd$: Subscription;
  finnAd: FinnAdvertisement;

  // These values are fetched by a request, since their values might change at any time
  public finnSectors: FinnSector[] = [];
  public finnDurations: FinnDuration[] = [];
  public finnIndustries: FinnIndustry[] = [];
  public finnOccupations: FinnOccupation[] = [];

  // form
  public invalid: boolean;

  public FINN_VALUES_STATE_LOADING: string = 'FINN_VALUES_STATE_LOADING';

  public disableUpdate = false;
  public isSubmitted: boolean;
  public companyForm: FormGroup;
  public headerForm: FormGroup;
  public workplaceForm: FormGroup;
  public contactForm: FormGroup;

  step: number = 0;
  expandedAll: boolean = true;
  formInvalidMessage: string = '';
  public disableButton = false;

  contacts: FormArray; // FinnContact[];
  errorMsg: any;
  tags: any[] = [];
  public occupations: FinnOccupation[] = [];
  public industries: FinnIndustry[] = [];
  public industriesErrorMsg = 'Minst en bransje er påkrevd.';
  public occupationsErrorMsg = 'Minst en stillingsfunksjon er påkrevd.';

  public loading: boolean;

  public showQuill = false;

  // autocomplete

  filteredIndustries: any = {};
  filteredOccupations: any = {};

  constructor(
    private _finnService: FinnService,
    public _userService: UserService,
    public _translate: TranslateService,
    public cs: ContentService,
    public _snackBar: MatSnackBar,
    private _paymentService: PaymentService,
    private _facebookService: FacebookService,
    private fb: FormBuilder,
    public $u: Utils,
    public dialogRef: MatDialogRef<FinnFormComponent>,
    private store: Store<fromRoot.State>
  ) {
    this._initializeForm();
    this._translate
      .get('generic_field_required')
      .pipe(take(1))
      .subscribe(error_string => {
        this.errorMsg = error_string;
      });
    this._translate
      .get('post_job_snackbar_form_invalid')
      .pipe(take(1))
      .subscribe(error_string => {
        this.formInvalidMessage = error_string;
      });
    this._translate
      .get('job_payment_services_finn_form_industry_error')
      .pipe(take(1))
      .subscribe(error_string => {
        this.industriesErrorMsg = error_string;
      });
    this._translate
      .get('job_payment_services_finn_form_occupation_error')
      .pipe(take(1))
      .subscribe(error_string => {
        this.occupationsErrorMsg = error_string;
      });
  }

  ngOnDestroy(): void {
    if (this.finnAd$) {
      this.finnAd$.unsubscribe();
    }
  }

  ngOnInit(): void {
    this.finnAd$ = this.store.select(getSelectedFinnForm).subscribe(finnAd => {
      if (!finnAd) {
        this.finnAd = new FinnAdvertisement(0);
        return;
      }
      this.finnAd = finnAd;
      this._finnService
        .getFinnAdvertisementFormValues()
        .pipe(take(1))
        .subscribe(
          (res: FinnAdvertisement) => {
            this._handleFinnFormValuesResponse(res);
            this._setFormValues(this.finnAd);
            this._setFormValuesByJob(this.finnAd);
          },
          error => {
            this.$u.hide(this.FINN_VALUES_STATE_LOADING);
          }
        );
    });
  }

  private _setValidators(
    group: FormGroup | FormArray,
    shouldMarkAsTouched: boolean = false,
    shouldRemoveValidators: boolean = false
  ): void {
    Object.keys(group.controls).forEach((key: string) => {
      const abstractControl = group.controls[key];

      if (
        abstractControl instanceof FormGroup ||
        abstractControl instanceof FormArray
      ) {
        this._setValidators(abstractControl, shouldMarkAsTouched);
      } else {
        if (shouldRemoveValidators) {
          abstractControl.setValidators(null);
        }
        if (shouldMarkAsTouched) {
          abstractControl.markAsTouched();
        }
        abstractControl.updateValueAndValidity();
      }
    });
    group.updateValueAndValidity();
  }

  public updateDescription($event: EditorText) {
    this.headerForm.patchValue({
      general_text: $event.rich,
    });
  }

  public updateIngress($event: EditorText) {
    this.companyForm.patchValue({
      ingress: $event.rich,
    });
  }

  private setFilteredIndustries(data?) {
    data.forEach((industry, i) => {
      this.filteredIndustries[i] = this.industriesList.controls[
        i
      ].valueChanges.pipe(
        startWith(''),
        map(value => this._filter(value, this.finnIndustries))
      );
    });
  }

  public blurAutocomplete(autocompletePanel: MatAutocompleteTrigger) {
    setTimeout(() => autocompletePanel.closePanel(), 130);
  }

  setFilteredOccupations(data?) {
    if (!data) {
      return this.finnOccupations.filter(
        array_el =>
          this.occupations.filter(
            occ => occ.specialization === array_el.specialization
          ).length === 0
      );
    }
    data.forEach((occupation, i) => {
      this.filteredOccupations[i] = this.occupationsList.controls[
        i
      ].valueChanges.pipe(
        startWith(''),
        map(value => {
          const newArr = this.finnOccupations.filter(
            array_el =>
              this.occupations.filter(
                occ => occ.specialization === array_el.specialization
              ).length === 0
          );
          return this._filter(value, newArr);
        })
      );
    });
  }

  isQuill(text: string) {
    return text.length > 0 && text[0] === '{' && text[text.length - 1] === '}';
  }

  private _setFormValuesByJob(finnAd: FinnAdvertisement) {
    let ingressHtml = finnAd.ingress || '';
    if (this.isQuill(finnAd.ingress)) {
      ingressHtml = this.quillGetHTML(finnAd.ingress);
    }
    let generalTextHtml = finnAd.general_text || '';
    if (this.isQuill(finnAd.general_text)) {
      generalTextHtml = this.quillGetHTML(finnAd.general_text);
    }
    this.showQuill = true;
    this.companyForm.patchValue({
      ingress: ingressHtml,
    });
    this.headerForm.patchValue({
      heading: finnAd.heading.replace(/(\r\n\t|\n|\r\t)|(<br>)/gm, ' '),
      general_text: generalTextHtml,
    });
  }

  private quillGetHTML(inputDelta) {
    if (!inputDelta) {
      return '';
    }
    const delta = JSON.parse(inputDelta);
    // another check, better to be safe than sorry
    if (!(delta && delta['ops'] && delta['ops'].length)) {
      return '';
    }
    const tempCont = document.createElement('div');
    new Quill(tempCont).setContents(delta['ops']);
    return tempCont.getElementsByClassName('ql-editor')[0].innerHTML;
  }

  private _filter(value: string, array: Array<any>) {
    if (value) {
      const filterValue = value.toLowerCase();
      return array.filter(option =>
        option.name.toLowerCase().includes(filterValue)
      );
    } else {
      return array;
    }
  }

  private _setFormValues(data: any) {
    this.companyForm.patchValue({
      company_name: data.company_name,
      ingress: data.ingress,
      company_url: data.company_url,
      company_facebook_url: data.company_facebook_url,
      company_linkedin_url: data.company_linkedin_url,
      company_twitter_url: data.company_facebook_url,
    });

    this.headerForm.patchValue({
      position: data.position,
      heading: data.heading,
      general_text: data.general_text,
      finn_form_tag_desc: data.finn_form_tag_desc,
      sector: data.sector,
      duration: data.duration,
      video_url: data.video_url,
      num_positions: data.num_positions,
    });

    this.workplaceForm.patchValue({
      city: data.city,
      address: data.address,
      zip_code: data.zip_code,
    });

    this._setContactList(data.contacts);
    this.showQuill = true;
  }

  private _handleFinnFormValuesResponse(data: FinnAdvertisement) {
    if (!data) {
      return;
    }
    this.finnDurations = data.durations;
    this.finnSectors = data.sectors;
    this.finnIndustries = data.industries;
    this.finnOccupations = data.occupations;
    if (this.finnAd.industries.length) {
      this.industries = [...this.finnAd.industries];
    } else {
      this.addIndustrySlot();
    }
    if (this.finnAd.occupations.length) {
      this.occupations = [...this.finnAd.occupations];
    } else {
      this.addOccupationSlot();
    }
    this.assignIndustriesControls();
    this.setFilteredIndustries(this.industries);
    this.assignOccupationsControls();
    this.setFilteredOccupations(this.occupations);
  }

  assignIndustriesControls() {
    this.industriesList.controls.splice(0, this.industriesList.controls.length);
    this.industries.forEach(industry => {
      this.industriesList.controls.push(
        new FormControl(industry.name, [
          Validators.required,
          this.isOnIndustriesListValidator.bind(this),
        ])
      );
    });
  }

  assignOccupationsControls() {
    this.occupationsList.controls.splice(
      0,
      this.occupationsList.controls.length
    );
    this.occupations.forEach(occupation => {
      this.occupationsList.controls.push(
        new FormControl(occupation.name, [
          Validators.required,
          this.isOnOccupationsListValidator.bind(this),
        ])
      );
    });
  }

  // Industry methods
  public addIndustrySlot(): void {
    if (this.industries.length < 3) {
      this.industriesList.push(
        new FormControl('', [
          Validators.required,
          this.isOnIndustriesListValidator.bind(this),
        ])
      );
      this.industries.push(new FinnIndustry(''));
      this.setFilteredIndustries(this.industries);
    }
  }

  public addIndustryAtIndex(index: number, value: string): void {
    this.industries[index] = new FinnIndustry(value);
  }

  public removeIndustryAtIndex(index: number): void {
    this.industries.splice(index, 1);
    this.industriesList.removeAt(index);
  }

  // Occupation methods
  public addOccupationSlot(): void {
    if (this.occupations.length < 3) {
      this.occupations.push(new FinnOccupation('', ''));
      this.occupationsList.push(
        new FormControl('', [
          Validators.required,
          this.isOnOccupationsListValidator.bind(this),
        ])
      );
      this.setFilteredOccupations(this.occupations);
    }
  }

  public addOccupationAtIndex(value, index): void {
    const occ = this.finnOccupations.find(
      occupation => occupation.general === value
    );
    this.occupations[index] = new FinnOccupation(
      occ.general,
      occ.specialization
    );
    this.setFilteredOccupations(this.occupations);
  }

  public removeOccupationAtIndex(index: number): void {
    this.occupations.splice(index, 1);
    this.occupationsList.removeAt(index);
  }

  private _setContactList(contacts: FinnContact[]) {
    if (!contacts) {
      return;
    }
    const contactList = contacts.map(contact => this.fb.group(contact));
    const contactsFormArray = this.fb.array(contactList);
    this.contactForm.setControl('contacts', contactsFormArray);
  }

  public removeContactAtIndex(index: number): void {
    this.contactList.removeAt(index);
  }

  public addEmptyContact() {
    this.contacts = this.contactForm.get('contacts') as FormArray;
    this.contacts.push(this._createContact());
  }

  // Tag methods
  public addTag(): void {
    if (
      !this.headerForm.controls.finn_form_tag_desc.value ||
      this.tags.length >= 5
    ) {
      return;
    }

    this.tags.push(
      new FinnTag(this.headerForm.controls.finn_form_tag_desc.value)
    );
    this.headerForm.controls.finn_form_tag_desc.patchValue('');
  }

  public removeTagAtIndex(index: number): void {
    this.tags.splice(index, 1);
  }

  public postFinnForm() {
    this.toggleOrderButton(true);
    this._setValidators(this.companyForm, true);
    this._setValidators(this.headerForm, true);
    this._setValidators(this.contactForm, true);
    this._setValidators(this.workplaceForm, true);
    if (
      this.companyForm.invalid ||
      this.headerForm.invalid ||
      this.contactForm.invalid ||
      this.workplaceForm.invalid ||
      !this.industries[0].value ||
      !this.occupations[0].general
    ) {
      this._snackBar.open(this.formInvalidMessage, null, {
        duration: 3000,
      });
      this.isSubmitted = true;

      if (this.companyForm.invalid) {
        this.setStep(0);
      } else if (this.headerForm.invalid || !!this.industries[0]) {
        this.setStep(1);
      } else if (this.contactForm.invalid || !!this.occupations[0]) {
        this.setStep(2);
      } else if (this.workplaceForm.invalid) {
        this.setStep(3);
      }
      this.toggleOrderButton();
      return;
    }
    this.loading = true;
    let finn_form_tag_desc, headerWithoutTagInput;
    ({ finn_form_tag_desc, ...headerWithoutTagInput } = this.headerForm.value);
    const newFinnAd = {
      ...this.finnAd,
      ...this.companyForm.value,
      ...headerWithoutTagInput,
      contacts: this.contactForm.controls.contacts.value,
      ...this.workplaceForm.value,
      occupations: this.occupations.filter(
        occupation => occupation.general || occupation.name
      ),
      tags: this.tags,
      industries: this.industries.filter(
        industry => industry.name || industry.value
      ),
      validated: true,
    };
    this.dialogRef.close(newFinnAd);
  }

  public setStep(index: number) {
    this.step = index;
  }

  public goBack() {
    this.dialogRef.close(this.finnAd);
  }

  private toggleOrderButton(disableButton = false) {
    this.disableButton = disableButton;
  }

  isOnIndustriesListValidator(control: AbstractControl) {
    return this.finnIndustries.findIndex(
      industry => industry.name === control.value
    ) < 0
      ? { notOnList: true }
      : null;
  }

  isOnOccupationsListValidator(control: AbstractControl) {
    return this.finnOccupations.findIndex(
      occupation => occupation.name === control.value
    ) < 0
      ? { notOnList: true }
      : null;
  }

  private _initializeForm() {
    this.companyForm = this.fb.group({
      company_name: ['', Validators.required],
      ingress: ['', Validators.required],
      company_url: ['', Validators.required],
      company_facebook_url: [''],
      company_linkedin_url: [''],
      company_twitter_url: [''],
      industries: this.fb.array([]),
    });

    this.headerForm = this.fb.group({
      position: ['', Validators.required],
      heading: ['', Validators.required],
      general_text: ['', Validators.required],
      finn_form_tag_desc: [''],
      salary: [''],
      sector: ['', Validators.required],
      duration: ['', Validators.required],
      video_url: [''],
      num_positions: [1],
      occupations: this.fb.array([]),
    });

    this.workplaceForm = this.fb.group({
      city: ['', Validators.required],
      address: ['', Validators.required],
      zip_code: [
        '',
        [Validators.required, Validators.minLength(4), Validators.maxLength(4)],
      ],
    });

    this.contactForm = this.fb.group({
      contacts: this.fb.array([this._createContact()]),
    });
  }

  private _createContact(): FormGroup {
    return this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      title: '',
      phone: '',
      linkedin_url: '',
      twitter_url: '',
    });
  }

  public trackByFn(index, item) {
    return index; // or item.id
  }

  // GETTERS
  get company_name() {
    return this.companyForm.get('company_name');
  }

  get ingress() {
    return this.companyForm.get('ingress');
  }

  get company_url() {
    return this.companyForm.get('company_url');
  }

  get company_facebook_url() {
    return this.companyForm.get('company_facebook_url');
  }

  get company_linkedin_url() {
    return this.companyForm.get('company_linkedin_url');
  }

  get company_twitter_url() {
    return this.companyForm.get('company_twitter_url');
  }

  get position() {
    return this.headerForm.get('position');
  }

  get num_positions() {
    return this.headerForm.get('num_positions');
  }

  get heading() {
    return this.headerForm.get('heading');
  }

  get general_text() {
    return this.headerForm.get('general_text');
  }

  get finn_form_tag_desc() {
    return this.headerForm.get('finn_form_tag_desc');
  }

  get salary() {
    return this.headerForm.get('salary');
  }

  get sector() {
    return this.headerForm.get('sector');
  }

  get duration() {
    return this.headerForm.get('duration');
  }

  get video_url() {
    return this.headerForm.get('video_url');
  }

  get workplace_city() {
    return this.workplaceForm.get('city');
  }

  get workplace_address() {
    return this.workplaceForm.get('address');
  }

  get workplace_zip_code() {
    return this.workplaceForm.get('zip_code');
  }

  get contactList(): FormArray {
    return <FormArray>this.contactForm.get('contacts');
  }

  get industriesList(): FormArray {
    return <FormArray>this.companyForm.get('industries');
  }

  get occupationsList(): FormArray {
    return <FormArray>this.headerForm.get('occupations');
  }

  navigateToFinn(url: string) {
    window.open(url);
  }
}
